import {
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query';
import { getDiscoverMPTerminals, getMPConnectionStatus, getMPUsersStores, getStoredMPTerminals } from '../../services/integration.service';
import { ICompany } from '../../interfaces/Company';
import { IPaymentTerminal } from './mutations';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';

export interface IMercadoPagoStore {
    id: string;
    name: string;
    date_creation: string;
    business_hours?: {
        monday?: { open: string; close: string; }[];
        tuesday?: { open: string; close: string; }[];
        wednesday?: { open: string; close: string; }[];
        thursday?: { open: string; close: string; }[];
        friday?: { open: string; close: string; }[];
        saturday?: { open: string; close: string; }[];
        sunday?: { open: string; close: string; }[];
    };
    location?: {
        address_line: string;
        reference: string;
        latitude: number;
        longitude: number;
    }
    external_id: string;
}
export interface IDiscoverTerminalResponse {
    provider: 'mercadopago';
    mp_pos_id: string;
    mp_store_id?: string;
    serial?: string;
    label?: string;
}
export interface IMercadoPagoCredentials {
    access_token_enc: string;
    refresh_token_enc: string;
    token_expires_at: Date;
    mp_user_id: string;
    public_key: string;
    live_mode: boolean;
    scopes: string[];
}

export interface IMercadoPagoConnectionStatusResponse {
    companyId: ICompany | string;
    provider: 'mercadopago';
    mercadopago?: IMercadoPagoCredentials;
    isActive: boolean;
    createdAt?: Date;
    updatedAt?: Date;
}

export const useGetMPConnectionStatus = (): UseQueryResult<IMercadoPagoConnectionStatusResponse> => useQuery({
    queryKey: ['mpConnectionStatus'],
    queryFn: async () => {
        try {
            const res = await getMPConnectionStatus();
            return res?.data?.data;
        } catch (e) {
            backendErrorsHandler(e); // THIS WILL SHOW THE ERROR NOTIFICATION
        }
    }
});


export const useGetDiscoverMPTerminals = (data: {
    mp_store_id: string;
}): UseQueryResult<IDiscoverTerminalResponse[]> => useQuery({
    queryKey: ['discoverMPTerminals', data.mp_store_id],
    queryFn: async () => {
        try {
            const res = await getDiscoverMPTerminals(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data?.data;
        } catch (e) {
            console.log('ERROR getDiscoverMPTerminals', e);
            throw e;
        }
    }
});

export const useGetMPUsersStores = (data: {
    user_id: string;
}, enabled: boolean = false): UseQueryResult<IMercadoPagoStore[]> => useQuery({
    queryKey: ['mpUsersStores', data.user_id],
    enabled,
    queryFn: async () => {
        try {
            const res = await getMPUsersStores(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data?.data;
        } catch (e) {
            console.log('ERROR getMPUsersStores', e);
            throw e;
        }
    }
});

export const useGetStoredMPTerminals = (data: {
    storeId: string;
}, enabled: boolean = false): UseQueryResult<IPaymentTerminal[]> => useQuery({
    enabled,
    queryKey: ['storedMPTerminals', data.storeId],
    queryFn: async () => {
        try {
            const res = await getStoredMPTerminals(data);
            if (!res?.data?.data) {
                throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res?.data?.data;
        } catch (e) {
            console.log('ERROR getStoredMPTerminals', e);
            throw e;
        }
    }
});